import i from 'i18next'
import lines from 'balanced-line-split'
import { FC, useEffect, useRef, useState } from 'react'
import Star from 'components/svg/Star'
import { FormulaParams } from 'lib/hooks/use-reviews'
import Link from 'next/link'
import { res } from 'components/Image'

const PAGE = 3

const clamp = ( num, min, max ) => Math.max( min, Math.min( num, max ) )

export type Props = {
	handle?: FormulaParams['handle']
	tags?: FormulaParams['tags']
	handles?: Map<string, {
		handle: string
		title: string
		images: {
			originalSrc: string
		}
	}>
	title?: string
	num: number
	average: number
	stars: [ number, number, number, number, number ]
	page?: number
}

export default function Reviews({
	handle,
	tags,
	title,
	average,
	num,
	stars,
	handles,
	page = PAGE,
}: Props ) {
	const [ show, setShow ] = useState( page )
	const [ rating, setRating ] = useState( 0 )
	const [ reviews, setReviews ] = useState( [] )
	const offset = useRef<string>( null )

	useEffect( () => {
		if (
			!reviews.length ||
			( reviews.length <= show + page && offset.current )
		) {
			fetch( '/api/reviews', {
				method: 'post',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify({
					handle,
					tags,
					page,
					offset: offset.current,
					rating: rating || null,
				}),
			})
				.then( ( result ) => result.json() )
				.then( ( result ) => {
					if ( result.reviews ) {
						setReviews( ( reviews ) => [ ...reviews, ...result.reviews ] )
						offset.current = result.offset || null
					}
				})
		}
	}, [ show, rating ] )

	const getProduct = ( review: any ) => {
		if ( review[ 'linked handles' ] ) {
			const handle = review[ 'linked handles' ]?.split( ',' ).shift()
			return handles.get( handle )
		}
	}

	return (
		<section className="container">
			{( !!title && <h2>{lines( title, 2 )}</h2> )}
			<div className="summary">
				<div className="meta">
					<div className="score">
						<span>{Math.round( average * 10 ) / 10}</span>/5
					</div>
					<div>
						<Star filled={clamp( average - 0, 0, 1 )} size={20} />
						<Star filled={clamp( average - 1, 0, 1 )} size={20} />
						<Star filled={clamp( average - 2, 0, 1 )} size={20} />
						<Star filled={clamp( average - 3, 0, 1 )} size={20} />
						<Star filled={clamp( average - 4, 0, 1 )} size={20} />
						<div>
							<small>
								{i.t( 'product.reviews.total', { count: num })}
							</small>
						</div>
					</div>
				</div>
				<div className={'counts' + ( rating ? ' filtered' : '' )}>
					{[ 5, 4, 3, 2, 1 ].map( score => (
						<a
							key={score}
							onClick={() => {
								setReviews( [] )
								setShow( page )
								setRating( r => ( r === score ? null : score ) )
								offset.current = null
							}}
							className={
								( stars[ score - 1 ] ? '' : 'disabled' ) +
								( rating === score ? ' active' : '' )
							}
						>
							<Star filled={clamp( score - 0, 0, 1 )} size={20} />
							<Star filled={clamp( score - 1, 0, 1 )} size={20} />
							<Star filled={clamp( score - 2, 0, 1 )} size={20} />
							<Star filled={clamp( score - 3, 0, 1 )} size={20} />
							<Star filled={clamp( score - 4, 0, 1 )} size={20} />
							<div className="bar">
								<div
									className="fill"
									style={{
										width: `${Math.round(
											( stars[ score - 1 ] / num ) * 100
										)}%`,
									}}
								/>
							</div>
						</a>
					) )}
				</div>
			</div>
			<div className="reviews">
				{reviews.slice( 0, show ).map( ( review, j ) => (
					getProduct( review )
						? <ReviewWithThumbnail key={j} review={review} product={getProduct( review )} />
						: <NormalReview key={j} review={review} />
				) )}
			</div>
			{show < reviews.length && (
				<p>
					<a className="cta" onClick={() => setShow( show + page )}>
						{i.t( 'product.reviews.more', { count: page })}
					</a>
				</p>
			)}
			<style jsx>{`
				.reviews :global( .review ),
				.summary {
					display: flex;
					align-items: stretch;
					padding: 100px 0;
					border-bottom: 1px solid #c6cdd6;
				}
				.summary:first-child {
					padding-top: 0;
				}
				.reviews :global( .review:last-child ) {
					padding-bottom: 0;
					border-bottom: none;
				}
				.reviews :global( .review .meta ),
				.summary .meta {
					flex: 0 0 25%;
					display: flex;
					flex-direction: column;
					justify-content: space-between;
				}
				.reviews :global( .review .text ),
				.summary .counts {
					flex: 1;
				}
				.reviews :global( .review h3 ),
				.reviews :global( .review p:first-child ) {
					margin-top: 0;
				}
				section :global( small ) {
					font-size: 15px;
					color: #8a8f95;
				}
				.counts > a {
					display: flex;
					align-items: center;
					margin-bottom: 6px;
					transition: opacity .5s;
				}
				.counts:hover > a,
				.counts.filtered > a {
					opacity: .5;
				}
				.counts:hover > a.disabled {
					pointer-events: none;
				}
				.counts:hover > a:not( .disabled ):hover,
				.counts > a.active {
					opacity: 1;
				}
				.bar {
					width: 210px;
					height: 16px;
					margin-left: 15px;
					border-radius: 1px;
  				background-color: #ebecee;
					overflow: hidden;
				}
				.bar .fill {
					height: 100%;
					border-radius: 1px;
  				background-color: #44474d;
				}
				section :global( strong.name ) {
					display: block;
					margin-bottom: 1em;
					font-size: 24px;
					font-weight: 500;
				}
				section :global( .score ) {
					font-size: 20px;
					margin-bottom: .3em;
				}
				section :global( .score span ) {
					font-size: 35px;
					line-height: 1;
				}
				section :global( p ) {
					font-size: 20px;
					line-height: 1.6;
				}
				section :global( p + * ) {
					margin-top: .5em;
				}
				@media ( max-width: 700px ) {
					.reviews :global( .review ),
					.summary {
						padding: 60px 0;
					}
					.summary {
						flex-wrap: wrap;
					}
					.reviews :global( .review .meta ),
					.summary .meta {
						flex: 1 0 auto;
					}
					.summary .counts {
						margin-top: 60px;
					}
					.reviews :global( .review ) {
						flex-wrap: wrap;
					}
					.reviews :global( .review .meta ) {
						flex: 1 0 100%;
						flex-direction: column-reverse;
					}
					.reviews :global( strong.name ) {
						margin-top: .5em;
					}
					.reviews :global( p ) {
						font-size: 16px;
					}
				}
				@media ( max-width: 600px ) {
					.summary .counts {
						flex: 0 0 100%;
					}
					.bar {
						width: auto;
						flex: 1;
					}
				}
			`}</style>
		</section>
	)
}

const NormalReview: FC<{ review: any }> = ({ review }) => (
	<div className="review">
		<div className="meta">
			<strong className="name">{review.name}</strong>
			<div>
				<div className="score">
					<span>{review.rating}</span>/5
				</div>
				<Star filled={clamp( review.rating - 0, 0, 1 )} size={16} />
				<Star filled={clamp( review.rating - 1, 0, 1 )} size={16} />
				<Star filled={clamp( review.rating - 2, 0, 1 )} size={16} />
				<Star filled={clamp( review.rating - 3, 0, 1 )} size={16} />
				<Star filled={clamp( review.rating - 4, 0, 1 )} size={16} />
				<div>
					<small suppressHydrationWarning>
						{/* eslint-disable-next-line max-len */}
						{new Intl.DateTimeFormat().format( new Date( review.date ) )}
					</small>
				</div>
			</div>
		</div>
		<div className="text">
			{!!review.title && <h3>{review.title}</h3>}
			{review.review.split( '\n' ).map( ( line, i ) => (
				<p key={i}>{line.trim()}</p>
			) )}
		</div>
	</div>
)

type ReviewWithThumbnailProps = {
	review: any,
	product: Props['handles'] extends Map<any, infer I> ? I : never
}

const ReviewWithThumbnail: FC<ReviewWithThumbnailProps> = ({ review, product }) => (
	<div className="review">
		<Link href={`/products/all/${product.handle}`} legacyBehavior>
			<a>
				<picture>
					<img
						src={res( product.images?.[ 0 ]?.originalSrc, '300x' )}
						width={300}
					/>
				</picture>
			</a>
		</Link>
		<div className="text">
			<h3>{product.title}</h3>
			<div className="stars">
				<Star filled={clamp( review.rating - 0, 0, 1 )} size={16} />
				<Star filled={clamp( review.rating - 1, 0, 1 )} size={16} />
				<Star filled={clamp( review.rating - 2, 0, 1 )} size={16} />
				<Star filled={clamp( review.rating - 3, 0, 1 )} size={16} />
				<Star filled={clamp( review.rating - 4, 0, 1 )} size={16} />
				<span>
					{new Intl.DateTimeFormat().format( new Date( review.date ) )}
				</span>
			</div>
			{review.review.split( '\n' ).map( ( line, i ) => (
				<p key={i}>{line.trim()}</p>
			) )}
			<p>
				<small>
					{review.name}
				</small>
			</p>
		</div>
		<style jsx>{`
			a {
				width: 30%;
			}
			picture {
				position: relative;
				margin-right: 100px;
				padding: 60px 0;
				background-color: #f7f7f7;
			}
			picture img {
				width: 50%;
				margin: auto;
				object-fit: contain;
				image-rendering: -webkit-optimize-contrast;
			}
			@media ( min-width: 500px ) and ( max-width: 1000px ) {
				a {
					width: 35%;
				}
				picture {
					margin-right: 60px;
					padding: 40px 0;
				}
			}
			@media ( max-width: 500px ) {
				a {
					width: 100%;
				}
				picture {
					margin-right: 0;
					margin-bottom: 20px;
					padding: 20px 0;
				}
				picture img {
					margin: 40px auto;
				}
			}

			.stars {
				margin-top: 10px;
			}

			.stars :global( svg ) {
				transform: translateY( 1px );
			}

			.stars span {
				margin-left: 1em;
				color: #8a8f95;
			}

			.stars + p {
				margin-top: 20px;
			}
		`}</style>
	</div>
)
